import { ChangeEvent, FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Input } from 'src/components/common/Input';
import { setBodyOverflow } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useResetSignUpError } from 'src/hooks/useResetSignupErrors';
import { initSdkLegitimuzRequest } from 'src/services/userApi';
import { setError } from 'src/store/user/actions';
import { RootState } from 'src/types/store-types';
import * as Yup from 'yup';

type IProps = {
  name: string;
  value: string;
  regexp: string;
  onChange: (e: any) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
};
const Ip_Input: FC<IProps> = (props) => {
  // Hooks
  const { t }: Translation = useTranslation();

  // Redux
  const _setErrorReq = useConnectedAction(setError);
  const resetSignUpError = useResetSignUpError();

  // Selectors
  const { errors: signUpErrors, cpfValue } = useSelector((state: RootState) => state.user);
  // YUP Validation
  const validationSchema = Yup.object().shape({
    ip: Yup.string().required(t('requiredField')).matches(new RegExp(props.regexp), t('fieldLength')),
  });

  const updateValue = useCallback(
    (ipAddress: string) => {
      handleValidate(ipAddress);
      props.onChange({
        target: {
          name: props.name,
          value: ipAddress,
        },
      });
    },
    [props]
  );

  useEffect(() => {
    const initSdk = async (): Promise<void> => {
      try {
        const data = {
          meta: { cpf: String(cpfValue) },
          deviceinfo: navigator.userAgent,
          hardware_concurrency: navigator.hardwareConcurrency,
        };

        const response = await initSdkLegitimuzRequest(data);

        if (response?.body?.ip_address) {
          updateValue(response.body.ip_address);
        }
        if (response?.message === 'error') {
          console.error('error', response?.errors);
          setBodyOverflow('set');
        }
      } catch (error: any) {
        console.error('Fraud Prevention Error:', error);
        updateValue('');
        setBodyOverflow('set');
      }
    };

    if (cpfValue) initSdk();
  }, [cpfValue]);

  const handleValidate = async (value: string): Promise<void> => {
    try {
      await validationSchema.validate({ ip: value }, { abortEarly: false });
      resetSignUpError('ip');
    } catch (error: any) {
      if (error.name === 'ValidationError') {
        _setErrorReq({ ...signUpErrors, ip: error.errors[0] });
      }
    }
  };

  // Actions
  const handleOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (signUpErrors.ip) {
      resetSignUpError('ip');
    }
    props.onChange(e);
  };

  const handleBlur = (): void => {
    handleValidate(props.value?.trim());
  };

  return <Input error={signUpErrors.ip} {...props} onChange={handleOnChange} onBlur={handleBlur} />;
};
export default Ip_Input;
